<template>
  <div id="app" :class="{ 'app--with-tabbar': showTabbar }">
    <template v-if="isConfigLoaded">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>

      <Teleport to="body">
        <van-tabbar
          v-if="showTabbar"
          route
          fixed
          active-color="#ffffff"
          inactive-color="#ffffff"
          class="custom-tabbar"
          style="z-index: 99"
        >
          <van-tabbar-item
            v-for="(item, index) in menuList"
            :key="index"
            :to="item.path"
            :icon="item.icon"
            >{{ item.name }}</van-tabbar-item
          >
        </van-tabbar>
      </Teleport>
    </template>
    <div v-else class="loading-container">
      <van-loading type="spinner" color="#1989fa" size="24px" vertical>
        加载中...
      </van-loading>
    </div>
  </div>
</template>

<script setup>
import { computed, provide, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { isShenBaoSolo } from "@/utils/shenbaoSolo";

const store = useStore();
const route = useRoute();

const overlayOpenCount = ref(0);

const setAppOverlayOpen = (open) => {
  if (open) {
    overlayOpenCount.value += 1;
  } else {
    overlayOpenCount.value = Math.max(0, overlayOpenCount.value - 1);
  }
};

provide("setAppOverlayOpen", setAppOverlayOpen);

// 判断配置是否加载完成
const isConfigLoaded = computed(() => {
  return !!store.state.config.webViewConfig;
});

const menuList = computed(() => {
  const config = store.state.config.webViewConfig;
  return config?.page_menu_list || [];
});

// 判断当前页面是否应该显示底部菜单
const shouldShowTabbar = computed(() => {
  // 外链独立落地：隐藏底部导航，避免跳入其他页
  if (isShenBaoSolo(route) || route.meta?.standalone) {
    return false;
  }

  const currentPath = route.path;
  const inMenuList = menuList.value.some((item) => item.path === currentPath);
  const isSubPage = currentPath.split("/").length > 2;

  return inMenuList && !isSubPage;
});

const showTabbar = computed(
  () => shouldShowTabbar.value && overlayOpenCount.value === 0
);
</script>

<style>
html,
body {
  margin: 0;
  background-color: #c5e6fe;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #c5e6fe;
}

#app.app--with-tabbar {
  /* 与 tabbar 高度对齐，避免内容与导航之间多出一截白边 */
  padding-bottom: 50px;
}

/* 底部导航：整条蓝底 + 白字白图标（选中/未选中一致） */
html body .van-tabbar.custom-tabbar {
  --van-tabbar-background: #2b7de9;
  --van-tabbar-item-text-color: #ffffff;
  --van-tabbar-item-active-color: #ffffff;
  --van-tabbar-height: 50px;
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  transform: none !important;
  margin: 0 !important;
  padding-bottom: env(safe-area-inset-bottom);
  background: #2b7de9 !important;
  background-color: #2b7de9 !important;
  border: none !important;
  box-shadow: none !important;
  z-index: 99 !important;
}

html body .van-tabbar.custom-tabbar::before,
html body .van-tabbar.custom-tabbar::after {
  display: none !important;
  border: none !important;
}

html body .van-tabbar.custom-tabbar .van-tabbar-item {
  flex: 1;
  background: transparent !important;
  background-color: transparent !important;
  color: #ffffff !important;
}

html body .van-tabbar.custom-tabbar .van-tabbar-item--active {
  background: transparent !important;
  background-color: transparent !important;
  color: #ffffff !important;
}

html body .van-tabbar.custom-tabbar .van-tabbar-item .van-icon,
html body .van-tabbar.custom-tabbar .van-tabbar-item--active .van-icon,
html body .van-tabbar.custom-tabbar .van-tabbar-item .van-tabbar-item__icon,
html body .van-tabbar.custom-tabbar .van-tabbar-item--active .van-tabbar-item__icon,
html body .van-tabbar.custom-tabbar .van-tabbar-item .van-tabbar-item__text,
html body .van-tabbar.custom-tabbar .van-tabbar-item--active .van-tabbar-item__text {
  color: #ffffff !important;
}

html body .van-tabbar.custom-tabbar .van-tabbar-item__text {
  font-size: 11px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
</style>
